import React, { useState, useEffect } from 'react';
import { Target, Users, Activity, TrendingUp, MapPin, Calendar, Award, Heart, BookOpen, Shield, Home } from 'lucide-react';

const RGHDProject = () => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
  }, []);

  const projectStats = [
    { number: '6,000', label: 'Girls Receiving MHM Kits', icon: Users },
    { number: '3,000', label: 'Adolescents Accessing SRH', icon: Heart },
    { number: '12', label: 'Schools with WASH Facilities', icon: Home },
    { number: '2', label: 'Health Centres Strengthened', icon: Shield }
  ];

  const keyComponents = [
    {
      icon: BookOpen,
      title: 'Menstrual Health Management',
      description: 'Comprehensive MHM support including reusable sanitary kits, girl-friendly WASH facilities, and education programs in schools and communities.',
      highlights: ['2 sets of reusable sanitary kits per girl', '12 girl-friendly WASH stalls with disposal bins', 'Teacher training in MHM and puberty education']
    },
    {
      icon: Heart,
      title: 'Youth-Friendly SRH Services', 
      description: 'Accessible sexual and reproductive health services including modern contraception and comprehensive sexuality education for adolescents.',
      highlights: ['Mobile clinic outreach days', 'Adolescent clinic corners', 'Individual counselling and referral pathways']
    },
    {
      icon: Shield,
      title: 'Post-Abortion Care Strengthening',
      description: 'Enhanced PAC capacity at health centres with WHO-recommended protocols, counselling, and immediate contraception provision.',
      highlights: ['Clinician training on PAC protocols', 'Contraceptive counselling at discharge', 'Adolescent-friendly confidential approaches']
    },
    {
      icon: Users,
      title: 'Community Engagement',
      description: 'Shifting community norms through male engagement, parent involvement, and traditional leader participation to support girls\' rights.',
      highlights: ['Village dialogue sessions', 'Father-daughter workshops', 'Cultural leader engagement programs']
    }
  ];

  const impactAreas = [
    { title: 'Menstrual Health & Dignity', description: 'Ensuring girls can manage menstruation safely and with dignity while staying in school' },
    { title: 'Sexual & Reproductive Health', description: 'Providing youth-friendly SRH services and comprehensive sexuality education' },
    { title: 'Post-Abortion Care', description: 'Timely, quality PAC services with contraceptive counselling and support' }
  ];

  return (
    <div className="project-page">
      {/* Bootstrap CSS */}
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet" 
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container position-relative">
          <div className="row align-items-center min-vh-70">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge">Flagship Project</div>
                <h1 className="hero-title">
                  Rakai Girls' Health & Dignity Project
                </h1>
                <p className="hero-subtitle">
                  One Outreach for All Strategy
                </p>
                <p className="hero-description">
                  An integrated cost-effective strategy to improve Menstrual Health, sexual & reproductive health (SRH) and Post-abortion care (PAC) access for girls and young women in Rakai district, Uganda.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-container">
                <div className="project-image">
                  <img 
                    src="/images/projects/rghd-project.jpg" 
                    alt="Rakai Girls Health and Dignity Project"
                    className="hero-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="image-placeholder">
                    <Heart size={60} />
                    <span>Project Image</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className={`stats-grid ${animateIn ? 'animate-in' : ''}`}>
                {projectStats.map((stat, index) => (
                  <div key={index} className="stat-card">
                    <div className="stat-icon">
                      <stat.icon size={32} />
                    </div>
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="overview-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="project-image-container">
                <img 
                  src="/images/projects/rghd-overview.jpg" 
                  alt="Project Overview"
                  className="project-overview-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder">
                  <Target size={80} />
                  <span>Project Overview</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="overview-content">
                <div className="section-badge">Project Overview</div>
                <h2 className="section-title">Integrated Health & Dignity Solutions</h2>
                <p className="section-description">
                  The RGHD Project addresses critical health challenges facing adolescent girls in Rakai district. 
                  With approximately 25% of young women giving birth by age 18 and significant gaps in menstrual health 
                  management, our integrated approach tackles multiple barriers simultaneously to improve health outcomes 
                  and educational retention.
                </p>
                
                <div className="impact-areas">
                  <h4 className="subsection-title">Key Focus Areas</h4>
                  {impactAreas.map((area, index) => (
                    <div key={index} className="impact-item">
                      <h5 className="impact-title">{area.title}</h5>
                      <p className="impact-description">{area.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Components */}
      <section className="components-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className={`section-header text-center ${animateIn ? 'animate-in' : ''}`}>
                <div className="section-badge">Project Components</div>
                <h2 className="section-title">How We Make Impact</h2>
                <p className="section-description">
                  Our integrated approach combines menstrual health management, youth-friendly SRH services, 
                  post-abortion care, and community engagement for comprehensive support.
                </p>
              </div>
            </div>
          </div>
          
          <div className="row">
            {keyComponents.map((component, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <div className="component-card">
                  <div className="component-icon">
                    <component.icon size={32} />
                  </div>
                  <div className="component-content">
                    <h3 className="component-title">{component.title}</h3>
                    <p className="component-description">{component.description}</p>
                    <ul className="component-highlights">
                      {component.highlights.map((highlight, idx) => (
                        <li key={idx}>
                          <TrendingUp size={16} />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="problem-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-header text-center">
                <div className="section-badge">Challenge Context</div>
                <h2 className="section-title">Addressing Critical Health Gaps</h2>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-lg-6 mb-4">
              <div className="problem-card">
                <div className="problem-stat">25%</div>
                <div className="problem-title">Adolescent Pregnancy Rate</div>
                <p className="problem-description">
                  Uganda has a very high rate of adolescent pregnancy with ≈25% of young women giving birth by age 18, 
                  placing rural districts like Rakai at heightened risk of school drop-out and poor health outcomes.
                </p>
              </div>
            </div>
            
            <div className="col-lg-6 mb-4">
              <div className="problem-card">
                <div className="problem-stat">28.4</div>
                <div className="problem-title">Abortions per 1,000 Women (15-19)</div>
                <p className="problem-description">
                  Estimates indicate substantial adolescent abortion activity with ~28.4 abortions per 1,000 women aged 15–19, 
                  highlighting the need for accessible, non-judgmental PAC and contraception services.
                </p>
              </div>
            </div>
            
            <div className="col-lg-6 mb-4">
              <div className="problem-card">
                <div className="problem-stat">20%+</div>
                <div className="problem-title">Unmet Contraception Need</div>
                <p className="problem-description">
                  Unmet need for modern contraception remains high with country estimates in the mid-20% range, 
                  emphasizing the need for improved access and youth-friendly counselling.
                </p>
              </div>
            </div>
            
            <div className="col-lg-6 mb-4">
              <div className="problem-card">
                <div className="problem-stat">Many</div>
                <div className="problem-title">Schools Lack MHM Systems</div>
                <p className="problem-description">
                  Menstrual hygiene is widely recognized as a barrier to girls' schooling and dignity, 
                  with many schools lacking comprehensive MHM systems including facilities, supplies, and information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Styling with #0891b2 Theme */}
      <style jsx>{`
        :root {
          --primary-color: #0891b2;
          --primary-dark: #0e7490;
          --primary-light: #22d3ee;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --text-light: #9ca3af;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
          --success-green: #10b981;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
          padding-top: 80px;
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          min-height: 70vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 30px 0;
        }

        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #fef7ed 0%, #fed7aa 100%);
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(245,158,11,0.05)"/><circle cx="80" cy="80" r="1.5" fill="rgba(245,158,11,0.03)"/></svg>') repeat;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          padding-right: 2rem;
        }

        .hero-badge {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(245, 158, 11, 0.1);
          color: var(--accent-orange);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 24px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .hero-title {
          font-size: clamp(2.5rem, 4vw, 4rem);
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 12px;
          line-height: 1.1;
        }

        .hero-subtitle {
          font-size: 1.5rem;
          color: var(--accent-orange);
          font-weight: 600;
          margin-bottom: 24px;
        }

        .hero-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          margin-bottom: 40px;
          line-height: 1.6;
          max-width: 600px;
        }

        .hero-image-container {
          position: relative;
          padding-left: 2rem;
        }

        .project-image {
          position: relative;
          width: 100%;
          height: 400px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-placeholder {
          display: none;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(135deg, var(--accent-orange), #d97706);
          color: white;
          text-align: center;
          gap: 16px;
        }

        /* Stats Section */
        .stats-section {
          padding: 80px 0;
          background: white;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 32px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        .stats-grid.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .stat-card {
          text-align: center;
          padding: 32px 24px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .stat-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, var(--accent-orange), #d97706);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 1rem;
          color: var(--text-gray);
          font-weight: 500;
        }

        /* Overview Section */
        .overview-section {
          padding: 20px 0;
          background: var(--light-bg);
        }

        .project-image-container {
          position: relative;
        }

        .project-overview-image {
          width: 100%;
          height: 500px;
          object-fit: cover;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .overview-content {
          padding-left: 48px;
        }

        .section-badge {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(245, 158, 11, 0.1);
          color: var(--accent-orange);
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .section-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 24px;
          line-height: 1.2;
        }

        .section-description {
          font-size: 1.1rem;
          color: var(--text-gray);
          margin-bottom: 40px;
          line-height: 1.6;
        }

        .impact-areas {
          margin-top: 48px;
        }

        .subsection-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 24px;
        }

        .impact-item {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-light);
        }

        .impact-item:last-child {
          border-bottom: none;
        }

        .impact-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--accent-orange);
          margin-bottom: 8px;
        }

        .impact-description {
          color: var(--text-gray);
          margin: 0;
        }

        /* Components Section */
        .components-section {
          padding: 20px 0;
          background: white;
        }

        .section-header {
          margin-bottom: 20px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        .section-header.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .component-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          border: 1px solid var(--border-light);
        }

        .component-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .component-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, var(--accent-orange), #d97706);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 24px;
        }

        .component-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
        }

        .component-description {
          color: var(--text-gray);
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .component-highlights {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .component-highlights li {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        .component-highlights li svg {
          color: var(--success-green);
          flex-shrink: 0;
        }

        /* Problem Section */
        .problem-section {
          padding: 20px 0;
          background: var(--light-bg);
        }

        .problem-card {
          background: white;
          padding: 40px;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          text-align: center;
        }

        .problem-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .problem-stat {
          font-size: 3rem;
          font-weight: 800;
          color: var(--accent-orange);
          margin-bottom: 16px;
        }

        .problem-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 16px;
        }

        .problem-description {
          color: var(--text-gray);
          line-height: 1.6;
          margin: 0;
        }

        .cta-card {
          text-align: center;
          padding: 80px 40px;
        }

        .cta-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: white;
          margin-bottom: 24px;
        }

        .cta-description {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 40px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .cta-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* Button Styles */
        .btn {
          padding: 16px 32px;
          border-radius: 12px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          border: 2px solid transparent;
          font-size: 1rem;
        }

        .btn-primary {
          background: var(--primary-color);
          border-color: var(--primary-color);
          color: white;
        }

        .btn-primary:hover {
          background: var(--primary-dark);
          border-color: var(--primary-dark);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(8, 145, 178, 0.3);
        }

        .btn-outline-light {
          background: transparent;
          border-color: white;
          color: white;
        }

        .btn-outline-light:hover {
          background: white;
          color: var(--accent-orange);
          transform: translateY(-2px);
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .overview-content {
            padding-left: 24px;
          }
          
          .hero-content {
            padding-right: 1rem;
          }
          
          .hero-image-container {
            padding-left: 1rem;
          }
        }

        @media (max-width: 992px) {
          .overview-content {
            padding-left: 0;
            margin-top: 48px;
          }
          
          .hero-content {
            padding-right: 0;
            margin-bottom: 48px;
          }
          
          .hero-image-container {
            padding-left: 0;
          }
        }

        @media (max-width: 768px) {
          .hero-section {
            padding: 20px 0;
          }
          
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 24px;
          }
          
          .component-card, .problem-card {
            padding: 32px 24px;
          }
          
          .cta-card {
            padding: 60px 24px;
          }
          
          .btn {
            padding: 14px 28px;
            font-size: 0.95rem;
          }
        }

        /* Animation Utilities */
        .animate-in {
          animation: slideInUp 0.8s ease-out forwards;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default RGHDProject;